import { DeadlineIcon, TimeHourglassIcon } from '@sketchnote/icons';
import { Avatar, Divider } from '@sketchnote/shared-components';
import { format } from 'date-fns';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { useLayout } from 'src/shared/hooks/use-layout';
import { getFormattedValue } from 'src/utils/getFormattedValue';

import { NotificationProps } from '../types';

export const NotificationTemplate = ({
  item,
  markAllAsRead = false,
  onClickNotification,
}: {
  item: NotificationProps;

  markAllAsRead?: boolean;
  onClickNotification?: (item: NotificationProps) => void;
}) => {
  const { url, read, date, topic, type, message, sender, workspace, detail } = item;
  const [isNotificationRead, setIsNotificationRead] = useState(false);
  const { decrementNotificationCount } = useLayout();
  useEffect(() => {
    setIsNotificationRead(read);
  }, [read]);

  const dateUpdated = date ? new Date(date) : new Date();
  //  'dd/MM/yyyy hh:mm:ss aa'
  const dateFormat = format(new Date(dateUpdated), 'dd/MM/yyyy');

  if (!Object.keys(renderMessageForType).includes(type)) {
    return <></>;
  }
  const handleNotificationClick = () => {
    if (!isNotificationRead) {
      decrementNotificationCount();
      setIsNotificationRead(true);
    }
    onClickNotification?.(item);
  };
  return (
    <div
      role="button"
      onClick={handleNotificationClick}
      style={{ color: '#525866' }}
      className={`w-full text-sm mb-4 p-12 rounded-lg  ${
        !(markAllAsRead || isNotificationRead) ? 'bg-blue-100' : 'bg-white'
      }`}
    >
      <div className="flex items-start ">
        {type === 'reminder' ? (
          <div className="border rounded-md p-1.5">
            <DeadlineIcon />
          </div>
        ) : type !== 'final_status' ? (
          <span className="border rounded-full p-4">
            <Avatar name={sender?.firstname} src={sender?.avatar} size="md" type="notification" />
          </span>
        ) : (
          <></>
        )}

        <div className="mb-6 ml-12 flex-grow items-center my-auto">
          <div>
            <div>
              {type !== 'reminder' && type !== 'final_status' && (
                <span>{sender?.firstname || 'User'}&nbsp;</span>
              )}
              {renderMessageForType[type] ? (
                <span>{renderMessageForType[type].trim()}&nbsp;</span>
              ) : (
                <span />
              )}
              {item?.detail?.action && type === 'approval_completed' && (
                <span>{item?.detail?.action}&nbsp;</span>
              )}
              {/* //Topic used for backward comp */}
              {detail && Object.keys(detail).length > 0 ? (
                <span className=" text-gray-850">
                  {detail?.location === 'board_task'
                    ? `Task: ${getFormattedValue(detail?.task) || 'Untitled'}`
                    : detail?.location === 'file'
                    ? `file: ${detail?.file || 'Untitled'}`
                    : detail?.location === 'workspace'
                    ? `workspace: ${detail?.workspace || 'Untitled'}`
                    : 'Untitled'}
                </span>
              ) : typeof topic === 'object' ? (
                <span className=" text-gray-850">
                  {' '}
                  {getFormattedValue(topic?.task) || 'Untitled'}
                </span>
              ) : (
                <span className=" text-gray-850"> {getFormattedValue(topic) || 'Untitled'}</span>
              )}
              {item?.detail?.action && type === 'final_status' && (
                <span>&nbsp;is&nbsp;{item?.detail?.action}&nbsp;</span>
              )}
            </div>
            <div className="text-xs text-gray-600 mt-6">
              <span className="text-gray-550"> {dateFormat}</span>

              {detail?.location === 'board_task' && detail?.file && (
                <span>
                  <span className="px-4 text-gray-500 font-bold">·</span>File |{' '}
                  <span className="inline-block max-w-160 align-bottom truncate">
                    {detail?.file}
                  </span>
                </span>
              )}

              {detail?.location !== 'workspace' && detail?.workspace && (
                <span>
                  <span className="px-2 text-gray-500 font-bold">·</span>Workspace |{' '}
                  <span className="inline-block max-w-160 align-bottom truncate">
                    {detail?.workspace}
                  </span>
                </span>
              )}
            </div>
          </div>

          {item?.detail?.message && (
            <>
              <Divider className="py-6 text-gray-600" />
              <p className="w-full">
                <ReactMarkdown plugins={[gfm]}>
                  {getFormattedValue(item?.detail?.message)}
                </ReactMarkdown>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const renderMessageForType = {
  mention_document: 'mentioned you in',
  mention_board: 'mentioned you in',
  share_task: 'shared',
  watch_task: 'updated',
  assign: 'assigned you a',
  assign_board_task: 'assigned you a',
  assign_document_task: 'assigned you a',
  invite_workspace: 'invited you to',
  invite_file: 'invited you to',
  invite: 'invited you to',
  request_change: 'requested change in',
  approval_completed: '',
  update_approvers: 'added you as an approver in',
  withdraw_request: 'withdrew approval request in',
  reassign_approver: 'reassigned you as an approver for',
  resolve_request_change: 'resolved change request in',
  update_add_approvers: 'added you as an approver for',
  update_remove_approvers: 'removed you as an approver for',
  update_minimum_approvals: 'updated minimum approvals for',
  final_status: '',
  reminder: 'Deadline reminder for',
  mention_chat: 'mentioned you in',
};
